// ============================================================================
// Contracts — New Contract create form. Inserts a Draft contract; users add
// line items + submit for finance from the detail page.
//
// Visible to: sales, account_manager, national_am, executive, superuser (RLS).
// ============================================================================

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { toNumOrNull } from '../../shared/coerce';
import { Button, ErrorState, PageHeader } from '../../shared/components';
import {
  AMENDMENT_TYPES,
  BILLING_MODELS,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  type BillingModel,
  type ContractType,
  type NewContractInput,
} from './types';
import {
  createContract,
  useAccountOptions,
  useFacilityOptions,
  useParentContractOptions,
} from './hooks/use-contract-data';
import { FacilityBillingLoader } from '../../shared/facility-billing';
import { errorMessage } from '../../shared/errors';

interface FormState {
  account_id: string;
  facility_id: string;
  parent_contract_id: string;
  contract_type: ContractType | '';
  billing_model: BillingModel | '';
  monthly_rate: string;
  hourly_rate: string;
  estimated_monthly_hours: string;
  start_date: string;
  end_date: string;
  scope_summary: string;
  scope_of_work: string;
  payment_terms: string;
  notes: string;
}

const INITIAL: FormState = {
  account_id: '',
  facility_id: '',
  parent_contract_id: '',
  contract_type: '',
  billing_model: '',
  monthly_rate: '',
  hourly_rate: '',
  estimated_monthly_hours: '',
  start_date: '',
  end_date: '',
  scope_summary: '',
  scope_of_work: '',
  payment_terms: 'Net 30',
  notes: '',
};

const parseAmount = (s: string): number | null => toNumOrNull(s);

export default function ContractForm() {
  const navigate = useNavigate();
  const { employee } = useAuth();
  const { accounts, isLoading: loadingAccounts } = useAccountOptions();
  const { facilities, isLoading: loadingFacilities } = useFacilityOptions();

  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Filter facilities to the selected account (or show all if no account yet).
  const facilityChoices = useMemo(() => {
    if (!form.account_id) return facilities;
    return facilities.filter((f) => f.account_id === form.account_id);
  }, [facilities, form.account_id]);

  // Phase 2b: amendment types can reference a parent contract on the account.
  const isAmendmentType =
    form.contract_type !== '' &&
    AMENDMENT_TYPES.includes(form.contract_type as ContractType);
  const parentOptions = useParentContractOptions(
    isAmendmentType ? form.account_id || null : null,
  );

  const canSubmit = useMemo(
    () =>
      Boolean(
        form.account_id &&
          form.contract_type &&
          form.billing_model &&
          !submitting &&
          employee?.employee_id,
      ),
    [form.account_id, form.contract_type, form.billing_model, submitting, employee?.employee_id],
  );

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit || !employee?.employee_id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: NewContractInput = {
        account_id: form.account_id,
        facility_id: form.facility_id || null,
        contract_type: form.contract_type as ContractType,
        billing_model: form.billing_model as BillingModel,
        monthly_rate: parseAmount(form.monthly_rate),
        hourly_rate: parseAmount(form.hourly_rate),
        estimated_monthly_hours: parseAmount(form.estimated_monthly_hours),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        scope_summary: form.scope_summary.trim() || null,
        scope_of_work: form.scope_of_work.trim() || null,
        payment_terms: form.payment_terms.trim() || 'Net 30',
        notes: form.notes.trim() || null,
        source_bid_id: null,
        source_opportunity_id: null,
        parent_contract_id: isAmendmentType
          ? form.parent_contract_id || null
          : null,
      };
      const result = await createContract(payload, employee.employee_id);
      if (result?.contract_id) {
        navigate(`/contracts/${result.contract_id}`);
      } else {
        navigate('/contracts');
      }
    } catch (err) {
      setSubmitError(
        errorMessage(err, 'Failed to create the contract.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <div className="mb-3">
        <button
          type="button"
          onClick={() => navigate('/contracts')}
          className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
        >
          <ArrowLeft className="h-4 w-4" /> Back to contracts
        </button>
      </div>

      <PageHeader
        title="New contract"
        meta="Draft a customer contract. Line items and approvals come next."
      />

      {submitError && (
        <div className="mb-4">
          <ErrorState title="Couldn't create the contract" body={submitError} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Account" required>
          <select
            value={form.account_id}
            onChange={(e) => {
              set('account_id', e.target.value);
              set('facility_id', '');
            }}
            disabled={loadingAccounts}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          >
            <option value="">
              {loadingAccounts ? 'Loading…' : 'Select an account'}
            </option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_name ?? a.account_id}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Facility (optional — leave blank for multi-facility contracts)">
          <select
            value={form.facility_id}
            onChange={(e) => set('facility_id', e.target.value)}
            disabled={loadingFacilities || !form.account_id}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          >
            <option value="">
              {loadingFacilities ? 'Loading…' : '— Account-wide —'}
            </option>
            {facilityChoices.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.facility_name}
                {f.market_region ? ` · ${f.market_region}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Contract type" required>
            <select
              value={form.contract_type}
              onChange={(e) =>
                set('contract_type', e.target.value as ContractType)
              }
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            >
              <option value="">Select type</option>
              {CONTRACT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {CONTRACT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Billing model" required>
            <select
              value={form.billing_model}
              onChange={(e) =>
                set('billing_model', e.target.value as BillingModel)
              }
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            >
              <option value="">Select model</option>
              {BILLING_MODELS.map((b) => (
                <option key={b} value={b}>
                  {b.replace('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {isAmendmentType && (
          <Field label="Parent contract (optional — the contract this one amends)">
            <select
              value={form.parent_contract_id}
              onChange={(e) => set('parent_contract_id', e.target.value)}
              disabled={!form.account_id}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            >
              <option value="">
                {form.account_id ? '— None —' : 'Select an account first'}
              </option>
              {parentOptions.map((p) => (
                <option key={p.contract_id} value={p.contract_id}>
                  {p.contract_number ?? p.contract_id} ·{' '}
                  {CONTRACT_TYPE_LABELS[p.contract_type] ?? p.contract_type} ·{' '}
                  {CONTRACT_STATUS_LABELS[p.status] ?? p.status}
                </option>
              ))}
            </select>
          </Field>
        )}

        {(form.billing_model === 'Monthly' || form.billing_model === 'Hybrid') && (
          <Field label="Monthly rate (USD)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.monthly_rate}
              onChange={(e) => set('monthly_rate', e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </Field>
        )}

        {/* AFB-0041 — management fee + usables live on the FACILITY record.
            Editable here once a facility is chosen (saves directly to the
            facility, independent of contract creation). */}
        {form.facility_id ? (
          <div className="rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-4">
            <span className="mb-2 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
              Management fee &amp; usables (facility record)
            </span>
            <FacilityBillingLoader facilityId={form.facility_id} />
          </div>
        ) : (
          <p className="text-xs text-tcs-text-muted">
            Management fee (default 3%) and usables are set on each facility
            record — pick a facility above to edit them here, or edit them on
            the facility pages for account-wide contracts.
          </p>
        )}

        {(form.billing_model === 'Hourly' || form.billing_model === 'Hybrid') && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Hourly rate (USD)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.hourly_rate}
                onChange={(e) => set('hourly_rate', e.target.value)}
                className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </Field>
            <Field label="Est. monthly hours">
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.estimated_monthly_hours}
                onChange={(e) =>
                  set('estimated_monthly_hours', e.target.value)
                }
                className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Start date">
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => set('start_date', e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </Field>
          <Field label="End date (optional)">
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => set('end_date', e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </Field>
        </div>

        <Field label="Scope summary (1 line)">
          <input
            type="text"
            value={form.scope_summary}
            onChange={(e) => set('scope_summary', e.target.value)}
            placeholder="e.g. Initial cleaning services contract — Ascentist Warrensburg"
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </Field>

        <Field label="Scope of work (full, markdown OK)">
          <textarea
            value={form.scope_of_work}
            onChange={(e) => set('scope_of_work', e.target.value)}
            rows={8}
            placeholder="# Scope of Work&#10;&#10;Nightly cleaning Sun–Fri after hours…"
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </Field>

        <Field label="Payment terms">
          <input
            type="text"
            value={form.payment_terms}
            onChange={(e) => set('payment_terms', e.target.value)}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </Field>

        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </Field>

        <div className="flex items-center gap-2 pt-2">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Creating…' : 'Create contract'}
          </Button>
          <button
            type="button"
            onClick={() => navigate('/contracts')}
            className="text-sm text-tcs-text-secondary hover:text-tcs-text"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}
function Field({ label, required, children }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
        {required && <span className="ml-0.5 text-st-danger-fg">*</span>}
      </span>
      {children}
    </label>
  );
}
